import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import imageCompression from "browser-image-compression";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COMPRESSION_OPTS = {
  maxSizeMB: 1.2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.82,
};

/**
 * Photo uploader with built-in client-side compression and a tiny thumbnail
 * strip. Can be used in single-file mode (max=1, returns a single URL via
 * onChange) or multi-file mode (returns a string[]).
 *
 * Props:
 *  - value: string | string[]    current url(s) (controlled)
 *  - onChange: (next) => void    fires with updated value
 *  - max: number = 1             maximum number of photos allowed
 *  - overflowMessage: string     toast shown when user tries to exceed max
 *  - label: string               button label
 *  - testId: string              data-testid for the file input
 */
export default function UploadPhotoButton({
  value,
  onChange,
  max = 1,
  overflowMessage,
  label,
  testId,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const single = max === 1;
  const photos = single
    ? value
      ? [value]
      : []
    : Array.isArray(value)
    ? value
    : [];

  const buttonLabel =
    label ||
    (single
      ? photos.length
        ? "Replace photo"
        : "Upload photo"
      : photos.length
      ? `Add more (${photos.length}/${max})`
      : `Upload photos (up to ${max})`);

  const uploadOne = async (file) => {
    let toSend = file;
    try {
      // Skip compression for tiny files / non-jpeg-png to avoid breaking gifs.
      if (file.size > 250 * 1024 && /image\/(jpeg|png|webp)/i.test(file.type)) {
        toSend = await imageCompression(file, COMPRESSION_OPTS);
      }
    } catch (err) {
      console.warn("[UploadPhotoButton] compression failed, uploading original:", err);
    }
    const fd = new FormData();
    fd.append("file", toSend, file.name);
    const { data } = await api.post("/uploads/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const handlePick = async (e) => {
    const list = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!list.length) return;

    const remaining = Math.max(0, max - photos.length);
    if (list.length > remaining) {
      toast.info(
        overflowMessage ||
          `Up to ${max} photo${max > 1 ? "s" : ""} per upload — more storage options coming soon!`
      );
    }
    const slice = list.slice(0, remaining);
    if (!slice.length) return;

    setBusy(true);
    try {
      const urls = [];
      for (const f of slice) {
        try {
          urls.push(await uploadOne(f));
        } catch (err) {
          if (err?.handled) continue; // already toasted (e.g. nsfw)
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        }
      }
      if (!urls.length) return;
      if (single) {
        onChange?.(urls[0]);
      } else {
        onChange?.([...(photos || []), ...urls]);
      }
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (idx) => {
    if (single) {
      onChange?.("");
      return;
    }
    const next = photos.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const canAddMore = photos.length < max;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={!single}
        className="hidden"
        data-testid={testId}
        onChange={handlePick}
      />
      <button
        type="button"
        disabled={busy || !canAddMore}
        onClick={() => inputRef.current?.click()}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className="w-full px-3 py-2.5 rounded-xl bg-tb-cream-2/40 border-2 border-dashed border-tb-cream-3 text-tb-navy font-ui text-sm flex items-center justify-center gap-2 tb-tap hover:bg-tb-cream-2/70 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        {busy ? "uploading…" : buttonLabel}
      </button>

      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {photos.map((src, i) => (
            <div
              key={src + i}
              data-testid={testId ? `${testId}-thumb-${i}` : undefined}
              className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-tb-cream-3 bg-tb-cream-2"
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={testId ? `${testId}-remove-${i}` : undefined}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-tb-navy/80 text-white flex items-center justify-center tb-tap"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
